<template>
  <el-dialog 
    title="修改储值密码" 
    :visible.sync="dialogVisible" 
    width="400px" 
    append-to-body
    @close="handleClose" 
    @opened="handleOpened"
    @keydown.native="handleDialogKeyDown"
  >
    <!-- 会员信息展示 -->
    <div 
      v-if="memberInfo"
      style="margin-bottom: 15px; padding: 10px; background: #f0f9ff; border-radius: 4px; border-left: 4px solid #409EFF;"
    >
      <div style="color: #409EFF; font-weight: bold; margin-bottom: 5px;">会员信息：</div>
      <div style="color: #666; font-size: 14px;">
        姓名：{{ memberInfo.customerName || '未设置' }}
      </div>
      <div style="color: #666; font-size: 14px;">
        手机号：{{ memberInfo.customerPhoneNumber || '未设置' }}
      </div>
    </div>

    <!-- 修改密码表单 -->
    <el-form 
      id="changePasswordForm" 
      :model="form" 
      :rules="rules" 
      ref="changePasswordFormRef"
      label-width="100px" 
      size="small"
      @keydown.native="handleFormKeyDown"
    >
      <el-form-item label="原密码" prop="oldPassword">
        <el-input 
          ref="oldPasswordInput" 
          v-model="form.oldPassword" 
          autocomplete="off" 
          type="password"
          placeholder="请输入原密码" 
          show-password 
          @keydown.down.native.prevent="focusNextInput('oldPasswordInput')"
          @keydown.up.native.prevent="focusLastInput('oldPasswordInput')"
          @keydown.enter.native="handleConfirm" 
        />
      </el-form-item>
      
      <el-form-item label="新密码" prop="newPassword">
        <el-input 
          ref="newPasswordInput" 
          v-model="form.newPassword" 
          autocomplete="off" 
          type="password"
          placeholder="请输入新密码" 
          show-password 
          @keydown.down.native.prevent="focusNextInput('newPasswordInput')"
          @keydown.up.native.prevent="focusLastInput('newPasswordInput')"
          @keydown.enter.native="handleConfirm" 
        />
      </el-form-item>
      
      <el-form-item label="确认密码" prop="confirmPassword">
        <el-input 
          ref="confirmPasswordInput" 
          v-model="form.confirmPassword" 
          autocomplete="off"
          type="password" 
          placeholder="请再次输入新密码" 
          show-password
          @keydown.down.native.prevent="focusNextInput('confirmPasswordInput')"
          @keydown.up.native.prevent="focusLastInput('confirmPasswordInput')"
          @keydown.enter.native="handleConfirm" 
        />
      </el-form-item>
    </el-form>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <!-- <el-button @click="handleCancel">取消</el-button> -->
      <el-button 
        ref="changePasswordConfirmButton" 
        type="primary" 
        @click="handleConfirm"
        @keydown.enter.native="handleConfirm"
      >
        确认修改
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'ChangePasswordDialog',
  props: {
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false
    },
    // 会员信息
    memberInfo: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      // 表单数据
      form: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      // 输入框顺序
      inputOrder: ['oldPasswordInput', 'newPasswordInput', 'confirmPasswordInput']
    };
  },
  computed: {
    // 弹窗显示状态的双向绑定
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    
    // 表单验证规则
    rules() {
      return {
        oldPassword: [
          { required: true, message: '请输入原密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ]
      };
    }
  },
  watch: {
    // 监听弹窗显示状态，重置表单
    visible: {
      handler(newVisible) {
        if (newVisible) {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    // 弹窗打开事件
    handleOpened() {
      this.$nextTick(() => {
        if (this.$refs.oldPasswordInput) {
          this.$refs.oldPasswordInput.focus();
        }
      });
      this.$emit('opened');
    },

    // 弹窗关闭事件
    handleClose() {
      this.resetForm();
      this.$emit('close');
    },

    // 取消按钮
    handleCancel() {
      this.dialogVisible = false;
    },

    // 确认修改密码
    handleConfirm() {
      this.$refs.changePasswordFormRef.validate((valid) => {
        if (!valid) return;
        
        const passwordData = {
          oldPassword: this.form.oldPassword,
          newPassword: this.form.newPassword,
          confirmPassword: this.form.confirmPassword
        };
        
        this.$emit('confirm', passwordData);
      });
    },

    // 重置表单
    resetForm() {
      this.form = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      
      // 清除验证状态
      if (this.$refs.changePasswordFormRef) {
        this.$refs.changePasswordFormRef.clearValidate();
      }
    },

    // 验证确认密码
    validateConfirmPassword(rule, value, callback) {
      if (value === '') {
        callback(new Error('请再次输入新密码'));
      } else if (value !== this.form.newPassword) {
        callback(new Error('两次输入密码不一致'));
      } else {
        callback();
      }
    },

    // 处理弹窗的键盘事件
    handleDialogKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialogVisible = false;
      }
    },

    // 处理表单的键盘事件
    handleFormKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialogVisible = false;
      }
    },

    // 聚焦到下一个输入框
    focusNextInput(currentRef) {
      const currentIndex = this.inputOrder.indexOf(currentRef);
      const nextIndex = (currentIndex + 1) % this.inputOrder.length;
      const nextRef = this.inputOrder[nextIndex];

      this.$nextTick(() => {
        if (this.$refs[nextRef]) {
          this.$refs[nextRef].focus();
        }
      });
    },

    // 聚焦到上一个输入框
    focusLastInput(currentRef) {
      const currentIndex = this.inputOrder.indexOf(currentRef);
      const lastIndex = currentIndex === 0 ? this.inputOrder.length - 1 : currentIndex - 1;
      const lastRef = this.inputOrder[lastIndex];

      this.$nextTick(() => {
        if (this.$refs[lastRef]) {
          this.$refs[lastRef].focus();
        }
      });
    }
  }
};
</script>

<style scoped>
::v-deep #changePasswordForm .el-form-item__label {
  color: #606266 !important;
}

.dialog-footer {
  text-align: right;
}

.dialog-footer .el-button {
  margin-left: 10px;
}
</style>
